import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import Spinner from 'components/common/Spinner';
import { getUserActivityLogs, ActivityLogsResponse, ActivityLogsQueryParams } from 'services/admin/accountsAnalysisService';
import { useTheme } from 'contexts/ThemeContext';
import { getErrorMessage } from 'utils/errors';
import {
  List,
  X,
  Inbox,
  Activity,
  Heart,
  MessageCircle,
  UserPlus,
  Mail,
  Film,
  FileText,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';

interface ActivityLogsModalProps {
  userId: string;
  onClose: () => void;
}

const ActivityLogsModal: React.FC<ActivityLogsModalProps> = ({ userId, onClose }) => {
  const { t } = useTranslation();
  const { isDark } = useTheme();
  const [loading, setLoading] = useState<boolean>(true);
  const [data, setData] = useState<ActivityLogsResponse | null>(null);

  // Pagination
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [limit] = useState<number>(20);

  // Filters
  const [actionType, setActionType] = useState<string>('');
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');

  // Theme tokens
  const mainBg = isDark ? '#0F172B' : '#FFFFFF';
  const cardBorder = isDark ? '#314158' : '#E2E8F0';
  const subtitleColor = isDark ? '#90A1B9' : '#62748E';
  const textPrimary = isDark ? '#E2E8F0' : '#1E293B';
  const textSecondary = isDark ? '#90A1B9' : '#64748B';
  const borderColor = isDark ? '#1D293D' : '#F1F5F9';
  const headerBg = isDark ? '#1D293D' : '#F8FAFC';
  const iconContainerBg = isDark ? '#1C398E' : '#EFF6FF';
  const iconColor = isDark ? '#51A2FF' : '#2B7FFF';
  const inputBg = isDark ? '#1D293D' : '#FFFFFF';
  const inputBorder = isDark ? '#314158' : '#D1D5DB';
  const inputText = isDark ? '#E2E8F0' : '#374151';

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const params: ActivityLogsQueryParams = {
        page: currentPage,
        limit,
      };

      if (actionType) params.actionType = actionType;
      if (startDate) params.startDate = startDate;
      if (endDate) params.endDate = endDate;

      const response = await getUserActivityLogs(userId, params);
      setData(response);
      setTotalPages(response.pagination.totalPages);
    } catch (error: unknown) {
      toast.error(getErrorMessage(error, t('admin.accountsAnalysis.errors.fetchLogsFailed')));
    } finally {
      setLoading(false);
    }
  }, [userId, currentPage, limit, actionType, startDate, endDate, t]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Reset page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [actionType, startDate, endDate]);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getActionTypeIcon = (type: string): { icon: React.ReactNode; color: string } => {
    if (!type) return { icon: <Activity size={18} />, color: isDark ? '#90A1B9' : '#6B7280' };
    switch (type.toLowerCase()) {
      case 'likes':
        return { icon: <Heart size={18} />, color: isDark ? '#F87171' : '#EF4444' };
      case 'comments':
        return { icon: <MessageCircle size={18} />, color: isDark ? '#51A2FF' : '#3B82F6' };
      case 'follows':
        return { icon: <UserPlus size={18} />, color: isDark ? '#4ADE80' : '#22C55E' };
      case 'messages':
        return { icon: <Mail size={18} />, color: isDark ? '#C084FC' : '#A855F7' };
      case 'stories':
        return { icon: <Film size={18} />, color: isDark ? '#FBBF24' : '#F59E0B' };
      case 'posts':
        return { icon: <FileText size={18} />, color: isDark ? '#2DD4BF' : '#14B8A6' };
      default:
        return { icon: <Activity size={18} />, color: isDark ? '#90A1B9' : '#6B7280' };
    }
  };

  const getActionTypeBadgeStyle = (type: string): React.CSSProperties => {
    if (!type) return { background: isDark ? '#314158' : '#F3F4F6', color: isDark ? '#90A1B9' : '#374151' };
    switch (type.toLowerCase()) {
      case 'likes':
        return { background: isDark ? '#7F1D1D' : '#FEE2E2', color: isDark ? '#F87171' : '#B91C1C' };
      case 'comments':
        return { background: isDark ? '#1C398E' : '#DBEAFE', color: isDark ? '#51A2FF' : '#1D4ED8' };
      case 'follows':
        return { background: isDark ? '#14532D' : '#DCFCE7', color: isDark ? '#4ADE80' : '#15803D' };
      case 'messages':
        return { background: isDark ? '#581C87' : '#F3E8FF', color: isDark ? '#C084FC' : '#7E22CE' };
      case 'stories':
        return { background: isDark ? '#78350F' : '#FEF3C7', color: isDark ? '#FBBF24' : '#B45309' };
      case 'posts':
        return { background: isDark ? '#134E4A' : '#CCFBF1', color: isDark ? '#2DD4BF' : '#0F766E' };
      default:
        return { background: isDark ? '#314158' : '#F3F4F6', color: isDark ? '#90A1B9' : '#374151' };
    }
  };

  return createPortal(
    <div
      className="fixed inset-0 z-[10000] flex items-center justify-center"
      style={{
        background: isDark ? 'rgba(2, 6, 24, 0.75)' : 'rgba(241, 245, 249, 0.75)',
        backdropFilter: 'blur(10px)',
      }}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      {/* Modal */}
      <div
        className="relative w-full max-w-4xl max-h-[80vh] overflow-hidden mx-4 flex flex-col"
        style={{ background: mainBg, borderRadius: '16px', boxShadow: isDark ? '0 25px 50px rgba(0,0,0,0.5)' : '0 25px 50px rgba(0,0,0,0.15)' }}
      >
        {/* Header */}
        <div
          className="px-6 py-4 flex items-center justify-between"
          style={{ background: headerBg, borderBottom: `1px solid ${cardBorder}` }}
        >
          <div className="flex items-center gap-3">
            <div
              className="w-10 h-10 rounded-lg flex items-center justify-center"
              style={{ background: iconContainerBg }}
            >
              <List size={20} style={{ color: iconColor }} />
            </div>
            <div>
              <h2 className="text-base font-semibold" style={{ color: textPrimary }}>
                {t('admin.accountsAnalysis.activityLogs') || 'Activity Logs'}
              </h2>
              {data && (
                <p className="text-xs" style={{ color: subtitleColor }}>
                  {data.user.name} - {data.pagination.total} {t('admin.accountsAnalysis.entries') || 'entries'}
                </p>
              )}
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg transition-colors"
            style={{ color: textSecondary }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = isDark ? '#314158' : '#F1F5F9';
              e.currentTarget.style.color = textPrimary;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'transparent';
              e.currentTarget.style.color = textSecondary;
            }}
          >
            <X size={18} />
          </button>
        </div>

        {/* Filters */}
        <div
          className="px-6 py-3"
          style={{ background: headerBg, borderBottom: `1px solid ${cardBorder}` }}
        >
          <div className="flex flex-wrap items-center gap-3">
            {/* Action Type */}
            <select
              value={actionType}
              onChange={(e) => setActionType(e.target.value)}
              className="px-3 py-1.5 rounded-md text-xs focus:outline-none focus:ring-2 focus:ring-blue-500"
              style={{ background: inputBg, border: `1px solid ${inputBorder}`, color: inputText }}
            >
              <option value="">{t('admin.accountsAnalysis.allActions') || 'All Actions'}</option>
              <option value="likes">{t('admin.accountsAnalysis.likes') || 'Likes'}</option>
              <option value="comments">{t('admin.accountsAnalysis.comments') || 'Comments'}</option>
              <option value="follows">{t('admin.accountsAnalysis.follows') || 'Follows'}</option>
              <option value="messages">{t('admin.accountsAnalysis.messages') || 'Messages'}</option>
              <option value="stories">{t('admin.accountsAnalysis.stories') || 'Stories'}</option>
              <option value="posts">{t('admin.accountsAnalysis.posts') || 'Posts'}</option>
            </select>

            {/* Date Range */}
            <div className="flex flex-wrap items-center gap-2">
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="px-2 py-1.5 rounded-md text-xs focus:outline-none focus:ring-2 focus:ring-blue-500 w-[130px]"
                style={{ background: inputBg, border: `1px solid ${inputBorder}`, color: inputText }}
              />
              <span className="text-xs" style={{ color: textSecondary }}>to</span>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="px-2 py-1.5 rounded-md text-xs focus:outline-none focus:ring-2 focus:ring-blue-500 w-[130px]"
                style={{ background: inputBg, border: `1px solid ${inputBorder}`, color: inputText }}
              />
            </div>

            {/* Clear */}
            <button
              onClick={() => {
                setActionType('');
                setStartDate('');
                setEndDate('');
              }}
              className="px-3 py-1.5 rounded-md text-xs transition-colors"
              style={{ color: textSecondary }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = isDark ? '#314158' : '#F1F5F9';
                e.currentTarget.style.color = textPrimary;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'transparent';
                e.currentTarget.style.color = textSecondary;
              }}
            >
              {t('common.clear') || 'Clear'}
            </button>
          </div>
        </div>

        {/* Content */}
        <div className={`overflow-y-auto flex-1 ${isDark ? 'scrollbar-themed-dark' : 'scrollbar-themed-light'}`}>
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Spinner size="large" text={t('common.loading') || 'Loading...'} />
            </div>
          ) : !data || data.logs.length === 0 ? (
            <div className="text-center py-20">
              <Inbox size={48} style={{ color: textSecondary, margin: '0 auto', marginBottom: '12px' }} />
              <p className="text-sm" style={{ color: textSecondary }}>{t('admin.accountsAnalysis.noLogsFound') || 'No activity logs found'}</p>
            </div>
          ) : (
            <div>
              {data.logs.map((log, index) => {
                const actionIcon = getActionTypeIcon(log.actionType);
                return (
                  <div
                    key={log._id}
                    className="px-6 py-4 transition-colors"
                    style={{
                      borderTop: index > 0 ? `1px solid ${borderColor}` : undefined,
                      cursor: 'default',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = isDark ? '#1D293D' : '#F8FAFC';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = 'transparent';
                    }}
                  >
                    <div className="flex items-start gap-4">
                      {/* Icon */}
                      <div
                        className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
                        style={{ background: isDark ? '#1D293D' : '#F3F4F6' }}
                      >
                        <span style={{ color: actionIcon.color }}>{actionIcon.icon}</span>
                      </div>

                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-4">
                          <div>
                            <p className="text-sm" style={{ color: textPrimary }}>
                              {log.actionDescription} {log.username && `@${log.username}`}
                            </p>
                            <p className="text-xs mt-1" style={{ color: textSecondary }}>
                              @{log.accountUsername}
                            </p>
                          </div>
                          <div className="flex-shrink-0 text-right">
                            <p className="text-xs" style={{ color: textSecondary }}>{formatDate(log.completedAt || log.createdAt)}</p>
                          </div>
                        </div>

                        {/* Badges */}
                        <div className="flex items-center gap-2 mt-2">
                          <span
                            className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium"
                            style={getActionTypeBadgeStyle(log.actionType)}
                          >
                            {log.actionType}
                          </span>
                          {log.comment && (
                            <span
                              className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium max-w-xs truncate"
                              style={{ background: isDark ? '#314158' : '#F3F4F6', color: isDark ? '#90A1B9' : '#4B5563' }}
                            >
                              {log.comment}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Pagination */}
        {data && data.pagination.total > 0 && (
          <div
            className="px-6 py-3 flex items-center justify-between"
            style={{ background: headerBg, borderTop: `1px solid ${cardBorder}` }}
          >
            <p className="text-xs" style={{ color: textSecondary }}>
              {t('admin.accountsAnalysis.showing') || 'Showing'} {(currentPage - 1) * limit + 1} -{' '}
              {Math.min(currentPage * limit, data.pagination.total)} {t('admin.accountsAnalysis.of') || 'of'}{' '}
              {data.pagination.total} {t('admin.accountsAnalysis.entries') || 'entries'}
            </p>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="px-3 py-1.5 rounded-md text-xs font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ background: 'transparent', border: `1px solid ${inputBorder}`, color: textPrimary }}
                onMouseEnter={(e) => {
                  if (currentPage !== 1) {
                    e.currentTarget.style.background = isDark ? '#314158' : '#F1F5F9';
                  }
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'transparent';
                }}
              >
                <ChevronLeft size={14} />
              </button>
              <span className="text-xs" style={{ color: textSecondary }}>
                {currentPage} / {totalPages}
              </span>
              <button
                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                className="px-3 py-1.5 rounded-md text-xs font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ background: 'transparent', border: `1px solid ${inputBorder}`, color: textPrimary }}
                onMouseEnter={(e) => {
                  if (currentPage !== totalPages) {
                    e.currentTarget.style.background = isDark ? '#314158' : '#F1F5F9';
                  }
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'transparent';
                }}
              >
                <ChevronRight size={14} />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>,
    document.body
  );
};

export default ActivityLogsModal;
